import {
    AppPlatform,
    AppStoreType,
    DatadogRumApplication,
    DatadogRumApplicationtype,
    IntegrationInstallationId,
    IntegrationType,
} from '../types';
import * as React from 'react';
import { FormInstance, FormListProps, Rule, RuleObject } from 'antd/lib/form';
import { FormLayout } from 'antd/lib/form/Form';
import { ListOperations } from 'rc-field-form/lib/List';
import { flatMap, reduce } from 'lodash';
import { NamePath } from 'antd/lib/form/interface';
import Input, { InputProps } from 'antd/lib/input';
import Select, { SelectProps } from 'antd/lib/select';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import Divider from 'antd/lib/divider';
import Form from 'antd/lib/form';
import FormItem, { FormItemProps } from 'antd/lib/form/FormItem';
import Switch, { SwitchProps } from 'antd/lib/switch';
import Checkbox, { CheckboxProps } from 'antd/lib/checkbox/Checkbox';
import { antGrey } from '../components/styles/globalStyles';

export enum FieldType {
    Input,
    Select,
    Custom,
    Switch,
    Checkbox,
    List,
    WrappedGroup,
}

// I get the prop types directly from the possible components
type PossibleFieldProps = SwitchProps & CheckboxProps & FormItemProps & InputProps & SelectProps;

export type ListItemWrapperFunction = (
    children: React.ReactNode,
    listOperations: ListOperations,
    errors: { errors: React.ReactNode[]; warnings: React.ReactNode[] },
) => React.ReactNode;

export type GroupWrapperFunction = (
    children: React.ReactNode,
    fields: FormFieldData[],
    renderFields: (fields: FormFieldData[]) => React.ReactNode,
) => React.ReactNode;

// then pick only the fields we are actually using
export interface FormFieldData
    extends Pick<
        PossibleFieldProps,
        | 'initialValue'
        | 'name'
        | 'disabled'
        | 'placeholder'
        | 'rules'
        | 'label'
        | 'style'
        | 'dependencies'
        | 'required'
        | 'onChange'
    > {
    fieldType: FieldType;

    data?: any;

    extraData?: any[];

    renderField?: (data: any, ...extraData: any[]) => React.ReactNode; // used for custom fields
    helpMessage?: React.ReactNode;
    extraFormItemProps?: FormItemProps;
    skipFormItemWrapper?: boolean;
    inputProps?: PossibleFieldProps;

    // group specific
    groupedFields?: FormFieldData[];
    groupChildrenWrapper?: GroupWrapperFunction;

    // list specific
    nestedListFields?: FormFieldData[];
    listChildrenWrapper?: ListItemWrapperFunction;

    // switch specific
    switchOnText?: string;
    switchOffText?: string;

    showDivider?: boolean;
}

export const integrationModalContentUsingFormInstance: (
    form: FormInstance,
    layout: FormLayout,
    integrationType: IntegrationType,
    integrationInstallationId: IntegrationInstallationId,
    fields: FormFieldData[],
    onFieldsChange: (integrationType: IntegrationType, form: FormInstance) => void,
    onFinish: (
        integrationInstallationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        form: FormInstance,
    ) => void,
    onValuesChanged?: (
        integrationInstallationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        form: FormInstance,
    ) => void,
) => React.ReactNode = (
    form,
    layout,
    integrationType,
    integrationInstallationId,
    fields,
    onFieldsChange,
    onFinish,
    onValuesChanged,
) => {
    const initialValues = {};

    fields.forEach((field) => {
        if (field.fieldType === FieldType.WrappedGroup) {
            field.groupedFields?.forEach((groupedField) => {
                initialValues[groupedField.name] = groupedField.initialValue ?? null;
            });
        } else {
            initialValues[field.name] = field.initialValue ?? null;
        }
    });

    return (
        <Form
            form={form}
            layout={layout}
            autoComplete="off"
            initialValues={initialValues}
            onFinish={() => onFinish(integrationInstallationId, integrationType, form)}
            onFieldsChange={() => onFieldsChange(integrationType, form)}
            onValuesChange={() => {
                if (onValuesChanged) {
                    onValuesChanged(integrationInstallationId, integrationType, form);
                }
            }}
        >
            {renderFormFields(fields)}
        </Form>
    );
};

export const automationAndNotificationSettingsModalContentUsingFormInstance: (
    form: FormInstance,
    fields: FormFieldData[],
    disableForSubscription: boolean,
    onFinish: (form: FormInstance) => void,
) => React.ReactNode = (form, fields, disableForSubscription, onFinish) => {
    const initialValues = {};

    fields.forEach((field) => {
        if (field.fieldType === FieldType.WrappedGroup) {
            field.groupedFields?.forEach((groupedField) => {
                initialValues[groupedField.name] = groupedField.initialValue ?? null;
            });
        } else {
            initialValues[field.name] = field.initialValue ?? null;
        }
    });

    return (
        <Form
            form={form}
            autoComplete="off"
            layout="vertical"
            initialValues={initialValues}
            onFinish={() => onFinish(form)}
            disabled={disableForSubscription}
        >
            {renderFormFields(fields)}
        </Form>
    );
};

interface SelectData {
    value: string;
    displayValue: string;
}

export const renderFormField: (field: FormFieldData) => React.ReactNode = (field) => {
    let innerContent: React.ReactNode;
    let hasFeedback = true;

    switch (field.fieldType) {
        case FieldType.Input:
            innerContent = (
                <Input
                    placeholder={field.placeholder}
                    onChange={field.onChange}
                    allowClear
                    disabled={field.disabled !== undefined ? field.disabled : false}
                    autoComplete="off"
                />
            );
            break;
        case FieldType.Select: {
            const data: SelectData[] = field.data;

            innerContent = (
                <Select
                    placeholder={field.placeholder ?? 'Select'}
                    onChange={field.onChange}
                    disabled={field.disabled !== undefined ? field.disabled : false}
                    {...field.inputProps}
                >
                    {data.map((option, index) => {
                        return (
                            <Select.Option value={option.value} key={index}>
                                {option.displayValue}
                            </Select.Option>
                        );
                    })}
                </Select>
            );
            break;
        }
        case FieldType.Custom: {
            innerContent = field.renderField(field.data, ...(field.extraData ?? []));
            break;
        }
        case FieldType.WrappedGroup: {
            const fields = field.groupedFields ?? [];

            const children = (
                <>
                    {fields?.map((groupedField) => {
                        return (
                            <React.Fragment key={groupedField.name}>{renderFormFields([groupedField])}</React.Fragment>
                        );
                    })}
                </>
            );

            if (field.groupChildrenWrapper) {
                innerContent = field.groupChildrenWrapper(children, fields, renderFormFields);
            } else {
                innerContent = children;
            }

            innerContent = (
                <>
                    {innerContent}
                    {field.helpMessage && <Typography.Text type="secondary">{field.helpMessage}</Typography.Text>}
                </>
            );

            return innerContent;
        }
        case FieldType.List: {
            const nestedListFields = field.nestedListFields ?? [];
            innerContent = (
                <div
                    style={{
                        width: '100%',
                    }}
                >
                    {field.showDivider && <Divider />}
                    <Space
                        direction="vertical"
                        style={{
                            width: '100%',
                        }}
                        size={14}
                    >
                        {field.label && (
                            <Typography.Text
                                style={{
                                    color: antGrey.grey8,
                                    fontSize: 14,
                                }}
                            >
                                <strong>{field.label}</strong>
                            </Typography.Text>
                        )}
                        <Form.List {...(field as Omit<FormListProps, 'children'>)}>
                            {(listFieldsData, listOperations, errors) => {
                                const nestedFieldsNode = listFieldsData.map((listFieldData, index) => {
                                    const nestedFields = nestedListFields.map((nestedFieldData) => {
                                        const name =
                                            nestedFieldData.fieldType === FieldType.Custom ?
                                                [listFieldData.name]
                                            :   [listFieldData.name, nestedFieldData.name];

                                        return {
                                            ...nestedFieldData,
                                            name,
                                            data: {
                                                ...nestedFieldData.data,
                                                listOperations,
                                                index,
                                                listFieldData,
                                            },
                                        };
                                    }) as FormFieldData[];
                                    return renderFormFields(nestedFields);
                                });

                                // if there is a wrapper function, use it
                                if (field.listChildrenWrapper) {
                                    return field.listChildrenWrapper(nestedFieldsNode, listOperations, errors);
                                }

                                return nestedFieldsNode;
                            }}
                        </Form.List>
                    </Space>
                </div>
            );

            return innerContent;
        }
        case FieldType.Switch:
            innerContent = (
                <Switch
                    checkedChildren={field.switchOnText ?? 'On'}
                    onChange={field.onChange}
                    unCheckedChildren={field.switchOffText ?? 'Off'}
                    disabled={field.disabled !== undefined ? field.disabled : false}
                />
            );
            hasFeedback = false;
            break;
        case FieldType.Checkbox:
            innerContent = (
                <Checkbox disabled={field.disabled} onChange={field.onChange}>
                    {field.data}
                </Checkbox>
            );
            hasFeedback = false;
            break;
    }

    const valueProps =
        field.fieldType === FieldType.Switch || field.fieldType === FieldType.Checkbox ?
            { valuePropName: 'checked' }
        :   {};

    const hasRequiredRule = !!field.rules?.find((rule) => !!(rule as RuleObject)?.required);

    return (
        <React.Fragment key={field.name}>
            {field.showDivider && <Divider />}
            {field.skipFormItemWrapper ?
                innerContent
            :   <FormItem
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    rules={field.rules}
                    extra={field.helpMessage}
                    dependencies={field.dependencies}
                    required={field.required || hasRequiredRule}
                    hasFeedback={hasFeedback}
                    style={{ marginTop: 12, ...field.style }}
                    initialValue={field.initialValue}
                    {...valueProps}
                    {...field.extraFormItemProps}
                >
                    {innerContent}
                </FormItem>
            }
        </React.Fragment>
    );
};

export const renderFormFields: (fields: FormFieldData[]) => React.ReactNode = (fields) => {
    return fields.map(renderFormField);
};

export const formValidAndRequiredFieldsNotEmpty: (form: FormInstance, requiredFields: NamePath[]) => boolean = (
    form,
    requiredFields,
) => {
    const errors = flatMap(form.getFieldsError().map((field) => field.errors));
    const allRequiredFieldsValues = form.getFieldsValue(requiredFields);

    const allFieldsHaveValue = reduce(
        allRequiredFieldsValues,
        (prevFieldsHaveValue, fieldValue) => {
            if (prevFieldsHaveValue) {
                return fieldValue !== null && fieldValue !== undefined;
            }

            return prevFieldsHaveValue;
        },
        true,
    );

    return allFieldsHaveValue && errors.length === 0;
};

export const filterOptionLabel = (input, option) => {
    if (!input) {
        return false;
    }
    return (option.label as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0;
};

export const filterOptionValue = (input, option) => {
    if (!input) {
        return false;
    }

    return (option.value as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0;
};

export const datadogRumApplicationDisplayableString: (application: DatadogRumApplication) => string = (application) => {
    let platform = '';
    switch (application.type) {
        case DatadogRumApplicationtype.Android:
            platform = 'Android';
            break;
        case DatadogRumApplicationtype.Flutter:
            platform = 'Flutter';
            break;
        case DatadogRumApplicationtype.Ios:
            platform = 'iOS';
            break;
        case DatadogRumApplicationtype.JavaScript:
            platform = 'JavaScript';
            break;
        case DatadogRumApplicationtype.ReactNative:
            platform = 'React Native';
            break;
    }
    let text = application.name + ' (' + platform + ')';
    if (!application.isActive) {
        text += ' - inactive   ';
    } else {
        text += '   ';
    }

    return text;
};

export const isValidDatadogRumApplicationTypeForAppPlatform: (
    platform: AppPlatform,
) => (application: DatadogRumApplication) => boolean = (platform) => (application) => {
    switch (application.type) {
        case DatadogRumApplicationtype.Android:
            return platform === AppPlatform.Android || platform === AppPlatform.AndroidSDK;
        case DatadogRumApplicationtype.Ios:
            return platform === AppPlatform.Ios || platform === AppPlatform.IosSDK;
        case DatadogRumApplicationtype.ReactNative:
        case DatadogRumApplicationtype.Flutter:
        case DatadogRumApplicationtype.JavaScript:
            return true;
        default:
            return false;
    }
};

export const BASE_BRANCH_FIELD_RULES = [
    {
        validator: (_, value) => {
            if (value && value.includes(' ')) {
                return Promise.reject('Branch cannot contain a space');
            }

            return Promise.resolve();
        },
    },
];

const isValidEmail = (email?: string) => {
    const trimmedValue = email?.trim();
    if (!trimmedValue) {
        return false;
    }
    const regex = /^[\w\-\.]+@([\w-]+\.)+[\w-]{2,6}$/;
    return regex.test(trimmedValue);
};

export const emailValidationRules = (emails: string[], required = true) => {
    const rules = [];
    if (required === true) {
        rules.push({ required: true, message: 'At least one email address is required' });
    }
    const enterValidEmailMessage = 'Please enter a valid email';

    rules.push(
        {
            validator: (_, value) => {
                if (Array.isArray(value)) {
                    for (const email of value) {
                        if (!isValidEmail(email)) {
                            return Promise.reject(enterValidEmailMessage);
                        }
                    }
                    return Promise.resolve();
                }

                if (typeof value === 'string') {
                    if (!isValidEmail(value)) {
                        return Promise.reject(enterValidEmailMessage);
                    } else {
                        return Promise.resolve();
                    }
                }
                return Promise.reject(enterValidEmailMessage);
            },
            message: enterValidEmailMessage,
        },
        {
            validator: (_, value) => {
                if (typeof value === 'string' && emails.includes(value)) {
                    return Promise.reject('Email already added');
                } else {
                    return Promise.resolve();
                }
            },
            message: 'Email already added',
        },
    );
    return rules;
};

export const validateFileNameForAppStoreType: (fileName: string, appStoreType: AppStoreType) => [boolean, string] = (
    fileName,
    appStoreType,
) => {
    switch (appStoreType) {
        case AppStoreType.Apple:
            return [fileName.endsWith('.ipa'), 'Artifact filenames must be of type .ipa'];
        case AppStoreType.Google:
        case AppStoreType.Huawei:
        case AppStoreType.Samsung:
            return [
                fileName.endsWith('.aab') || fileName.endsWith('.apk'),
                'Artifact filenames must be of type .aab or .apk',
            ];
        case AppStoreType.Amazon:
            return [fileName.endsWith('.apk'), 'Artifact filenames must be of type .apk'];
        default:
            return [true, 'Artifact filenames must be of the correct type'];
    }
};

export const stagedRolloutPercentageInputValidationRules: Rule[] = [
    {
        validator(_rule, value) {
            if (value === 0 || value >= 100) {
                return Promise.reject();
            }

            return Promise.resolve();
        },
        message:
            'Staged rollout percentage must be greater than 0 and less than 100. Up to 6 decimal points are allowed.',
    },
];

export const filterOptionPrefixMatch = (input, option) => {
    if (!input) return false;
    const text = option?.children?.toString() || option?.label?.toString();
    return text?.toLowerCase().startsWith(input.toLowerCase()) ?? false;
};
